import React, {
  memo,
  type ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  Menu,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';

// import cx from 'classnames';
import { SodAdvancedFilters } from '../../../../../shared/components';
import SodSearch from '../../../../../shared/components/SodControls/SodSearch';
import {
  ABORT_ERROR,
  DEBOUNCE_TIME_FILTER_SEARCH,
  DEFAULT_AD_FILTER_PARAMS,
  VIOLATIONS_TAB_DETAILS_EXPANDER,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { violationsTabActions } from '../../../../ViolationsTab';
import { detailsActions } from '../../../data-store/detailsSlice';

interface ViolationsTabDetailsConflictsHeaderProps {
  endpointNames: string[];
  isDisabled: boolean;
}

/**
 * ViolationsTabDetailsConflictsHeader
 * @param {ViolationsTabDetailsConflictsHeaderProps} props - Component props
 * @param {string} props.endpointNames - To have all endpoints in function to use in entitlement filter
 * @param {ReactElement} props.isDisabled - Loading state
 * @returns {ReactElement} Rendered component
 */
function ViolationsTabDetailsConflictsHeader({
  endpointNames,
  isDisabled,
}: ViolationsTabDetailsConflictsHeaderProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const { filters, activeFilters } = useTypedSelector(
    (state) => state.aag.details.violationsTab.conflicts.controls
  );

  const [expanderOptions, setExpanderOptions] = useState(
    VIOLATIONS_TAB_DETAILS_EXPANDER
  );
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);

  // Expander
  const handleChange = (selectedOption: MenuOption) => {
    setExpanderOptions((currentOptions) =>
      currentOptions.map((option) => ({
        ...option,
        isChecked: option.value === selectedOption.value,
      }))
    );

    dispatch(detailsActions.setViolationsTabExpandKey(selectedOption.value));
  };

  /// For filters
  const isClearBtn = useMemo(() => activeFilters.length > 0, [activeFilters]);
  const [fetchEntitlements] = violationsTabActions.useGetEntitlementsMutation();

  const abortRef = useRef<((reason: string) => void) | null>(null);
  const lastRequestedKeyRef = useRef<string | null>(null);

  const handleFilterSearch = useCallback(
    async <T extends string>(
      key: T,
      searchText: T
    ): Promise<MenuOption[] | undefined | false> => {
      // Abort shoild only happen when the filter is same for search
      if (abortRef.current && lastRequestedKeyRef.current === key) {
        abortRef.current('New Search');
      }

      lastRequestedKeyRef.current = key;

      try {
        Logger.log('//TODO: Endpoint names should be used', endpointNames);

        const controller = fetchEntitlements({
          ...DEFAULT_AD_FILTER_PARAMS,
          search: searchText,
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const options = response as MenuOption[];

        return options;
      } catch (error) {
        const err = error as { name: string; message: string };

        Logger.log('error', err.message);

        if (err.name === ABORT_ERROR) {
          return false;
        }

        return undefined;
      }
    },
    [fetchEntitlements, endpointNames]
  );

  // For search
  const handleSearch = useCallback(
    (searchText: string) => {
      dispatch(detailsActions.setViolationsTabSearckKey(searchText));
    },
    [dispatch]
  );

  // For options click in menu
  const handleOptionsClick = useCallback(
    (key: string, method: string, selectedOptions: MenuOption[]) => {
      if (selectedOptions.length === 0) {
        const newActiveFilters = activeFilters.filter(
          (filter) => filter.value !== key
        );

        dispatch(
          detailsActions.setViolationsTabActiveFilters(newActiveFilters)
        );

        return;
      }

      const isFilterAvailable = activeFilters.find(
        (filter) => filter.value === key
      );
      const newActiveFilters = isFilterAvailable
        ? activeFilters.map((filter) => {
            if (filter.value === key) {
              return {
                ...filter,
                options: selectedOptions,
              };
            }

            return filter;
          })
        : [
            ...activeFilters,
            {
              value: key,
              method,
              options: selectedOptions,
            },
          ];

      dispatch(detailsActions.setViolationsTabActiveFilters(newActiveFilters));
    },
    [dispatch, activeFilters]
  );

  // For clear button press
  const handleClearBtn = useCallback(() => {
    dispatch(detailsActions.setViolationsTabActiveFilters([]));
  }, [dispatch]);

  return (
    <Box
      tag='div'
      className='Sod-ViolationsTabDetailsConflicts-viewControlsWrapper'>
      <Box tag='div' className='Sod-ViolationsTabDetailsConflicts-viewControls'>
        <Typography
          kind='h3'
          className='Sod-ViolationsTabDetailsConflicts-viewControlsHeading'>
          {l('Conflicting Job Functions')}
        </Typography>
        <Box className='Sod-ViolationsTabDetailsConflicts-viewControlsButtons'>
          <Menu
            options={expanderOptions.map((option) => ({
              ...option,
              label: l(option.label),
            }))}
            onChange={handleChange}
            trigger={
              <ButtonSelect
                kind='rectangle'
                label={l('Expand/Collapse')}
                size='medium'
                prefixIcon={<Icon kind='ChevronExpandAll' />}
                isVisualCueEnabled={false}
                isDisabled={isDisabled}
              />
            }
          />
          <ButtonSelect
            label={l('Filter')}
            ariaLabel={l('Filter')}
            kind='rectangle'
            onClick={() => {
              setIsFilterBarOpen((prev) => !prev);
            }}
            isOpen={isFilterBarOpen}
            prefixIcon={<Icon kind='Filter' />}
            BadgeComp={
              isClearBtn ? (
                <Badge kind='dot' color='primary' size='small' />
              ) : null
            }
            isDisabled={isDisabled}
            isVisualCueEnabled={false}
          />
          <SodSearch
            searchPlaceholder={l('Search')}
            inlineMesssage={l('Type more than 2 characters to see results.')}
            searchDebounce={DEBOUNCE_TIME_FILTER_SEARCH}
            isDisabled={isDisabled}
            handleSearch={handleSearch}
          />
        </Box>
      </Box>
      <SodAdvancedFilters
        filters={filters}
        activeFilters={activeFilters}
        maxEnabledFilterCount={6}
        isClearBtn={isClearBtn}
        searchDebounce={DEBOUNCE_TIME_FILTER_SEARCH}
        handleFilterChange={() => {}}
        handleFilterSearch={handleFilterSearch}
        handleOptionsClick={handleOptionsClick}
        handleClearBtn={handleClearBtn}
        className={
          isFilterBarOpen
            ? 'SodAdvancedFilters--isOpen'
            : 'SodAdvancedFilters--isClose'
        }
        isDisabled={isDisabled}
        isAddOrRemoveBtn={false}
      />
    </Box>
  );
}

export default memo(ViolationsTabDetailsConflictsHeader);
